import React from 'react';
import { Table, Form, Dropdown } from 'react-bootstrap';
import { FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { AppButton } from '../ui';

/**
 * Common DataTable Component
 * 
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Array of column definitions: 
 *   { key: string, label: string, render: (row, index) => ReactNode, sortable: boolean, width: string }
 * @param {Object} pagination - { page, pageSize, total, onPageChange }
 * @param {Object} filters - { searchValue, onSearchChange, customFilters }
 * @param {Array} bulkActions - Array of bulk action buttons
 * @param {Function} onRowClick - Callback for row click
 * @param {Function} rowClassName - (row) => string, optional per-row CSS class
 * @param {Boolean} selectable - Show checkboxes for selection
 * @param {Array} selectedRows - Array of selected row IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {String} emptyMessage - Message to show when no data
 * @param {Boolean} loading - Show loading state
 */
const DataTable = ({
  data = [],
  columns = [],
  pagination = null,
  filters = null,
  bulkActions = null,
  onRowClick = null,
  rowClassName = null,
  selectable = false,
  selectedRows = [],
  onSelectionChange = () => {},
  emptyMessage = "No data found",
  loading = false,
  hoverable = true,
  striped = false,
  bordered = false,
  renderExpandedRow = null,
}) => {
  const toRowId = (row, index) => {
    const raw = row.id ?? row._id;
    if (raw == null) return index;
    if (typeof raw === 'object') return JSON.stringify(raw);
    return raw;
  };

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const allIds = data.map((row, index) => toRowId(row, index));
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleRowCheck = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedRows, id]);
    } else {
      onSelectionChange(selectedRows.filter(rowId => rowId !== id));
    }
  };

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;
  const currentPage = pagination?.page || 1;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="data-table-wrapper">
      {/* Filters Section */}
      {filters && (
        <div className="data-table-filters">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {/* Search */}
            {filters.searchPlaceholder && (
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder={filters.searchPlaceholder}
                  value={filters.searchValue || ''}
                  onChange={(e) => filters.onSearchChange?.(e.target.value)}
                  className="search-input"
                />
              </div>
            )}

            {/* Custom Filter Dropdowns */}
            {filters.customFilters?.map((filter, index) => (
              <Dropdown key={index}>
                <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown">
                  {filter.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {filter.options?.map((option, idx) => (
                    <Dropdown.Item 
                      key={idx}
                      onClick={() => filter.onChange?.(option.value)}
                      active={filter.value === option.value}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ))}
          </div>

            {/* Bulk Actions */}
          {bulkActions && selectedRows.length > 0 && (
            <div className="bulk-actions">
              {bulkActions.map((action, index) => {
                const bsVariantMap = { primary: 'default', danger: 'destructive', secondary: 'secondary', warning: 'secondary', success: 'default', dark: 'default', light: 'ghost', link: 'ghost' };
                return (
                  <AppButton
                    key={index}
                    variant={bsVariantMap[action.variant] || 'default'}
                    size="sm"
                    onClick={() => action.onClick?.(selectedRows)}
                  >
                    {action.icon && <span className="me-2">{action.icon}</span>}
                    {action.label}
                  </AppButton>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table Section */}
      <div className="data-table-container">
        <Table 
          hover={hoverable} 
          striped={striped}
          bordered={bordered}
          responsive 
          className="data-table mb-0"
        >
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '50px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleCheckAll}
                  />
                </th>
              )}
              {columns.map((col, index) => (
                <th 
                  key={index}
                  style={{ width: col.width }}
                  className={col.sortable ? 'sortable' : ''}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-5 text-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowId = toRowId(row, rowIndex);
                const isSelected = selectedRows.includes(rowId);
                
                return (
                  <React.Fragment key={rowId}>
                  <tr 
                    onClick={() => onRowClick?.(row)}
                    className={[
                      onRowClick ? 'cursor-pointer' : '',
                      typeof rowClassName === 'function' ? rowClassName(row) : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {selectable && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleRowCheck(rowId, e.target.checked)}
                        />
                      </td>
                    )}
                    {columns.map((col, colIndex) => (
                      <td key={colIndex}>
                        {col.render ? col.render(row, rowIndex) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {renderExpandedRow && renderExpandedRow(row, rowIndex)}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Section */}
      {pagination && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            {pagination.total === 0 ? (
              <span>No entries</span>
            ) : (
              <span>
                Showing {((currentPage - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(currentPage * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} entries
              </span>
            )}
          </div>
          
          <div className="pagination-controls">
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
              className="me-2"
            >
              <FaChevronLeft size={12} />
            </AppButton>
            
            {getPageNumbers().map((pageNum, index) => (
              pageNum === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <AppButton
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => pagination.onPageChange?.(pageNum)}
                  className="me-2"
                >
                  {pageNum}
                </AppButton>
              )
            ))}
            
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange?.(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight size={12} />
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
